/**
 * An audio row's details, filed the way the library is browsed (Dax, 2026-09-23): music, a sound effect
 * or a voiceover, then the route's own words. Music takes up to three genres and three moods and can be
 * marked trending; an effect takes one category and the setting it belongs in; a voiceover its voice
 * artist (Dax, 2026-09-29). Every change autosaves; the pills read like the Tagging page's, blue when
 * applied and white to add.
 */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  MUSIC_GENRES, MUSIC_MOODS, SFX_CATEGORIES, SFX_SETTINGS, audioRoute,
  type AudioKind, type MusicGenre, type MusicMood, type SfxCategory, type SfxSetting,
} from '@studio/contracts';
import { SaveStatus } from '@/components/save-status';
import { useAutosave } from '@/hooks/use-autosave';
import { trpc } from '@/lib/trpc';

/** Genres and moods a row may carry at once: enough to be found, few enough to mean something. */
const MAX_PER_LIST = 3;

export interface AudioDetailsValue {
  id: string;
  name: string;
  kind: AudioKind;
  genres: MusicGenre[];
  moods: MusicMood[];
  sfxCategory: SfxCategory | null;
  setting: SfxSetting | null;
  trending: boolean;
  tags: string[];
  artist: string | null;
}

export function AudioDetails({ initial, credit, onClose, onSaved }: {
  initial: AudioDetailsValue;
  /** The artist, the effects library it came from, or a voiceover's clip, shown for context. */
  credit: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [v, setV] = useState(initial);
  const [tagText, setTagText] = useState(initial.tags.join(', '));
  const update = useMutation(trpc.compose.updateBed.mutationOptions({ onSuccess: onSaved }));
  const autosave = useAutosave((next: AudioDetailsValue) => update.mutateAsync({
    bedId: next.id,
    ...(next.name.trim() ? { name: next.name.trim() } : {}),
    kind: next.kind,
    genres: next.genres,
    moods: next.moods,
    sfxCategory: next.sfxCategory,
    setting: next.setting,
    trending: next.trending,
    tags: next.tags,
    ...(audioRoute(next.kind) === 'voice' ? { artist: next.artist?.trim() || null } : {}),
  }));
  const change = (patch: Partial<AudioDetailsValue>) => {
    const next = { ...v, ...patch };
    setV(next);
    autosave.schedule(next);
  };
  const route = audioRoute(v.kind);
  /** A pill list's toggle: on to off, or off to on while there is room. */
  const flip = <T extends string>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((x) => x !== item) : list.length < MAX_PER_LIST ? [...list, item] : list;

  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal audio-details" role="dialog" aria-label="Audio details">
        <div className="row between">
          <h2>Audio details</h2>
          <SaveStatus state={autosave.state} error={autosave.error} />
        </div>
        <label className="field"><span className="sub">Name</span>
          <input autoFocus value={v.name} aria-label="Audio name" maxLength={120} onChange={(e) => change({ name: e.target.value })} />
        </label>
        {credit && <span className="sub">{credit}</span>}
        <div className="seg audio-route" role="radiogroup" aria-label="Music, sound effect or voiceover">
          <button type="button" role="radio" aria-checked={route === 'music'} className={route === 'music' ? 'on' : ''}
            onClick={() => route !== 'music' && change({ kind: 'bed', sfxCategory: null, setting: null })}>Music</button>
          <button type="button" role="radio" aria-checked={route === 'sfx'} className={route === 'sfx' ? 'on' : ''}
            onClick={() => route !== 'sfx' && change({ kind: 'sfx', genres: [], moods: [], trending: false })}>Sound effect</button>
          <button type="button" role="radio" aria-checked={route === 'voice'} className={route === 'voice' ? 'on' : ''}
            onClick={() => route !== 'voice' && change({ kind: 'voice', genres: [], moods: [], trending: false, sfxCategory: null, setting: null })}>Voiceover</button>
        </div>
        {route === 'voice' ? (
          <label className="field"><span className="sub">Voice artist</span>
            <input value={v.artist ?? ''} aria-label="Voice artist" maxLength={200} placeholder="Who is speaking" onChange={(e) => change({ artist: e.target.value })} />
          </label>
        ) : route === 'music' ? (
          <>
            <PillPick label="Genre" all={MUSIC_GENRES} chosen={v.genres} onFlip={(g) => change({ genres: flip(v.genres, g) })} />
            <PillPick label="Mood" all={MUSIC_MOODS} chosen={v.moods} onFlip={(m) => change({ moods: flip(v.moods, m) })} />
            <button type="button" className="tick" aria-pressed={v.trending} onClick={() => change({ trending: !v.trending })}>
              <i className={v.trending ? 'on' : ''}>{v.trending && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i>
              Trending audio
            </button>
          </>
        ) : (
          <div className="row audio-sfx-fields">
            <label className="field"><span className="sub">Category</span>
              <select value={v.sfxCategory ?? ''} aria-label="Sound effect category" onChange={(e) => change({ sfxCategory: (e.target.value || null) as SfxCategory | null })}>
                <option value="">None</option>
                {SFX_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="field"><span className="sub">Setting</span>
              <select value={v.setting ?? ''} aria-label="Sound effect setting" onChange={(e) => change({ setting: (e.target.value || null) as SfxSetting | null })}>
                <option value="">None</option>
                {SFX_SETTINGS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
        )}
        <label className="field"><span className="sub">Tags</span>
          <input value={tagText} placeholder="kitchen, crisp, jingle" aria-label="Tags, comma separated"
            onChange={(e) => {
              setTagText(e.target.value);
              change({ tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) });
            }} />
        </label>
        <div className="row audio-details-foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
}

/** One fixed list as pills: blue with an x when chosen, white with a plus to add. */
function PillPick<T extends string>({ label, all, chosen, onFlip }: { label: string; all: readonly T[]; chosen: T[]; onFlip: (item: T) => void }) {
  return (
    <div className="audio-pills">
      <span className="sub">{label} · up to {MAX_PER_LIST}</span>
      <div className="tag-wrap">
        {all.map((item) => chosen.includes(item) ? (
          <span key={item} className="tg">
            {item}
            <button type="button" className="tg-x" aria-label={`Remove ${item}`} onClick={() => onFlip(item)}>×</button>
          </span>
        ) : (
          <button key={item} type="button" className="tg sug" disabled={chosen.length >= MAX_PER_LIST} onClick={() => onFlip(item)}>+ {item}</button>
        ))}
      </div>
    </div>
  );
}
